import type { SavedCharacter, SavedEnemy, SavedPlot, SavedRelationship, SavedWikipedia, SavedItem, SavedCommunity, SavedSociety, SavedSummary, SummaryResult } from '@creanizer/types'
import { convertOldDungeonToNew } from './converters/dungeonConverter'

/**
 * キャラクターの完全な詳細コンテキストを生成
 * 世界観ビルダーや他のジェネレータで参照時に使用
 */
export function generateCharacterContext(character: SavedCharacter): string {
  const c = character.result
  let context = `【キャラクター：${c.name}】\n`
  
  // 基本情報
  context += `◆基本情報\n`
  context += `  名前: ${c.name}\n`
  context += `  年齢: ${c.age}\n`
  context += `  性別: ${c.gender}\n`
  context += `  役割: ${c.role}\n`
  context += `  概要: ${c.summary}\n\n`
  
  // 性格
  if (c.personality) {
    context += `◆性格\n`
    if (c.personality.traits?.length > 0) {
      context += `  特性: ${c.personality.traits.join('、')}\n`
    }
    if (c.personality.strengths?.length > 0) {
      context += `  強み: ${c.personality.strengths.join('、')}\n`
    }
    if (c.personality.weaknesses?.length > 0) {
      context += `  弱点: ${c.personality.weaknesses.join('、')}\n`
    }
    if (c.personality.fears?.length > 0) {
      context += `  恐れ: ${c.personality.fears.join('、')}\n`
    }
    if (c.personality.desires?.length > 0) {
      context += `  欲求: ${c.personality.desires.join('、')}\n`
    }
    if (c.personality.values?.length > 0) {
      context += `  価値観: ${c.personality.values.join('、')}\n`
    }
    if (c.personality.likes?.length) {
      context += `  好きなもの: ${c.personality.likes.join('、')}\n`
    }
    if (c.personality.dislikes?.length) {
      context += `  嫌いなもの: ${c.personality.dislikes.join('、')}\n`
    }
    if (c.personality.goals?.length) {
      context += `  目標: ${c.personality.goals.join('、')}\n`
    }
    context += '\n'
  }
  
  // 背景
  if (c.background) {
    context += `◆背景\n`
    if (c.background.species) {
      context += `  種族・民族: ${c.background.species}\n`
    }
    if (c.background.language) {
      context += `  言語・方言: ${c.background.language}\n`
    }
    if (c.background.birthplace) {
      context += `  出身地: ${c.background.birthplace}\n`
    }
    if (c.background.family) {
      context += `  家族構成: ${c.background.family}\n`
    }
    if (c.background.education) {
      context += `  教育: ${c.background.education}\n`
    }
    if (c.background.occupation) {
      context += `  職業: ${c.background.occupation}\n`
    }
    if (c.background.significantEvents?.length > 0) {
      context += `  重要な出来事: ${c.background.significantEvents.join('、')}\n`
    }
    if (c.background.relationships?.length > 0) {
      context += `  人間関係: ${c.background.relationships.join('、')}\n`
    }
    if (c.background.secrets && c.background.secrets.length > 0) {
      context += `  秘密: ${c.background.secrets?.join('、')}\n`
    }
    if (c.background.backstoryNarrative) {
      context += `  バックストーリー: ${c.background.backstoryNarrative}\n`
    }
    context += '\n'
  }
  
  // 外見
  if (c.appearance) {
    context += `◆外見\n`
    if (c.appearance.height) {
      context += `  身長: ${c.appearance.height}\n`
    }
    if (c.appearance.weight) {
      context += `  体重: ${c.appearance.weight}\n`
    }
    if (c.appearance.build) {
      context += `  体型: ${c.appearance.build}\n`
    }
    if (c.appearance.hairColor && c.appearance.hairStyle) {
      context += `  髪: ${c.appearance.hairColor}の${c.appearance.hairStyle}\n`
    }
    if (c.appearance.eyeColor) {
      context += `  瞳の色: ${c.appearance.eyeColor}\n`
    }
    if (c.appearance.facialFeatures) {
      context += `  顔立ち: ${c.appearance.facialFeatures}\n`
    }
    if (c.appearance.clothing) {
      context += `  服装: ${c.appearance.clothing}\n`
    }
    if (c.appearance.distinctiveMarks) {
      context += `  特徴的な印: ${c.appearance.distinctiveMarks}\n`
    }
    if (c.appearance.overallImpression) {
      context += `  全体的な印象: ${c.appearance.overallImpression}\n`
    }
    if (c.appearance.voiceQuality) {
      context += `  声質: ${c.appearance.voiceQuality}\n`
    }
    if (c.appearance.posture) {
      context += `  姿勢: ${c.appearance.posture}\n`
    }
    context += '\n'
  }
  
  // 行動パターン
  if (c.behavior) {
    context += `◆行動パターン\n`
    if (c.behavior.firstPerson) {
      context += `  一人称: ${c.behavior.firstPerson}\n`
    }
    if (c.behavior.speechPattern) {
      context += `  口調: ${c.behavior.speechPattern}\n`
    }
    if (c.behavior.catchphrases?.length) {
      context += `  口癖: ${c.behavior.catchphrases.join('、')}\n`
    }
    if (c.behavior.mannerisms?.length > 0) {
      context += `  癖: ${c.behavior.mannerisms.join('、')}\n`
    }
    if (c.behavior.habits?.length > 0) {
      context += `  習慣: ${c.behavior.habits.join('、')}\n`
    }
    if (c.behavior.socialStyle) {
      context += `  社交スタイル: ${c.behavior.socialStyle}\n`
    }
    if (c.behavior.decisionMaking) {
      context += `  意思決定: ${c.behavior.decisionMaking}\n`
    }
    if (c.behavior.stressResponse) {
      context += `  ストレス反応: ${c.behavior.stressResponse}\n`
    }
    if (c.behavior.conflictResolution) {
      context += `  対立解決: ${c.behavior.conflictResolution}\n`
    }
    context += '\n'
  }
  
  // 成長・発展
  if (c.development) {
    context += `◆成長・発展\n`
    if (c.development.currentState) {
      context += `  現在の状態: ${c.development.currentState}\n`
    }
    if (c.development.growthPotential?.length > 0) {
      context += `  成長の可能性: ${c.development.growthPotential.join('、')}\n`
    }
    if (c.development.internalConflicts?.length > 0) {
      context += `  内的葛藤: ${c.development.internalConflicts.join('、')}\n`
    }
    if (c.development.externalChallenges?.length > 0) {
      context += `  外的課題: ${c.development.externalChallenges.join('、')}\n`
    }
    if (c.development.arcSuggestions?.length > 0) {
      context += `  アーク提案: ${c.development.arcSuggestions.join('、')}\n`
    }
    context += '\n'
  }
  
  // 感情表現
  if (c.emotionalExpression) {
    context += `◆感情表現\n`
    if (c.emotionalExpression.joy) context += `  喜び: ${c.emotionalExpression.joy}\n`
    if (c.emotionalExpression.anger) context += `  怒り: ${c.emotionalExpression.anger}\n`
    if (c.emotionalExpression.sadness) context += `  哀しみ: ${c.emotionalExpression.sadness}\n`
    if (c.emotionalExpression.enjoyment) context += `  楽しみ: ${c.emotionalExpression.enjoyment}\n`
    if (c.emotionalExpression.emotionalControl) context += `  感情抑制: ${c.emotionalExpression.emotionalControl}\n`
    if (c.emotionalExpression.stressRelief) context += `  ストレス発散: ${c.emotionalExpression.stressRelief}\n`
    context += '\n'
  }

  // 対人関係パターン
  if (c.interpersonalPatterns) {
    context += `◆対人関係パターン\n`
    if (c.interpersonalPatterns.firstMeetingBehavior) context += `  初対面: ${c.interpersonalPatterns.firstMeetingBehavior}\n`
    if (c.interpersonalPatterns.trustCriteria) context += `  信頼基準: ${c.interpersonalPatterns.trustCriteria}\n`
    if (c.interpersonalPatterns.romanticView) context += `  恋愛観: ${c.interpersonalPatterns.romanticView}\n`
    if (c.interpersonalPatterns.groupPosition) context += `  集団での立場: ${c.interpersonalPatterns.groupPosition}\n`
    context += '\n'
  }

  // 思考プロセス
  if (c.thinkingProcess) {
    context += `◆思考プロセス\n`
    if (c.thinkingProcess.problemSolvingApproach) context += `  問題解決: ${c.thinkingProcess.problemSolvingApproach}\n`
    if (c.thinkingProcess.crisisJudgment) context += `  危機判断: ${c.thinkingProcess.crisisJudgment}\n`
    if (c.thinkingProcess.intuitionLogicBalance) context += `  直感/論理: ${c.thinkingProcess.intuitionLogicBalance}\n`
    context += '\n'
  }

  // 日常生活
  if (c.dailyLife) {
    context += `◆日常生活\n`
    if (c.dailyLife.routine) context += `  日課: ${c.dailyLife.routine}\n`
    if (c.dailyLife.importantItems?.length > 0) context += `  大切なもの: ${c.dailyLife.importantItems.join('、')}\n`
    context += '\n'
  }

  // 信念
  if (c.beliefs) {
    context += `◆信念・哲学\n`
    if (c.beliefs.lifePhilosophy) context += `  人生哲学: ${c.beliefs.lifePhilosophy}\n`
    if (c.beliefs.worldview) context += `  世界観: ${c.beliefs.worldview}\n`
    if (c.beliefs.coreValues) context += `  価値観: ${c.beliefs.coreValues}\n`
    context += '\n'
  }

  // 専門性
  if (c.expertise) {
    context += `◆専門性\n`
    if (c.expertise.mainField) context += `  専門分野: ${c.expertise.mainField}\n`
    if (c.expertise.practicalSkills?.length > 0) context += `  実務能力: ${c.expertise.practicalSkills.join('、')}\n`
    context += '\n'
  }

  // 関係性詳細
  if (c.relationshipDetails) {
    context += `◆関係性詳細\n`
    if (c.relationshipDetails.respectedPeople?.length > 0) context += `  尊敬する人: ${c.relationshipDetails.respectedPeople.join('、')}\n`
    if (c.relationshipDetails.rivals?.length > 0) context += `  ライバル: ${c.relationshipDetails.rivals.join('、')}\n`
    context += '\n'
  }

  // サンプル台詞
  if (c.sampleDialogues?.length > 0) {
    context += `◆サンプル台詞\n`
    c.sampleDialogues.slice(0, 5).forEach(d => {
      context += `  「${d.line}」\n`
      context += `  （${d.situation}、${d.tone}${d.category ? `、${d.category}` : ''}）\n`
    })
    context += '\n'
  }
  
  // 作者向けメモ
  if (c.authorNotes?.length > 0) {
    context += `◆作者向けメモ\n`
    c.authorNotes.forEach(note => {
      context += `  ・${note}\n`
    })
    context += '\n'
  }
  
  // プロットフック
  if (c.plotHooks?.length > 0) {
    context += `◆プロットフック\n`
    c.plotHooks.forEach(hook => {
      context += `  ・${hook}\n`
    })
  }
  
  return context.trim()
}

/**
 * エネミーの完全な詳細コンテキストを生成
 * 世界観ビルダーや他のジェネレータで参照時に使用
 */
export function generateEnemyContext(enemy: SavedEnemy): string {
  const e = enemy.result
  let context = `【エネミー：${e.basicInfo.name}】\n`
  
  // 基本情報
  context += `◆基本情報\n`
  context += `  名前: ${e.basicInfo.name}\n`
  if (e.basicInfo.title) {
    context += `  称号: ${e.basicInfo.title}\n`
  }
  if (e.basicInfo.aliases && e.basicInfo.aliases.length > 0) {
    context += `  別名: ${e.basicInfo.aliases?.join('、')}\n`
  }
  context += `  タイプ: ${e.basicInfo.type}\n`
  context += `  カテゴリ: ${e.basicInfo.category}\n`
  context += `  脅威度: ${e.basicInfo.threat}\n`
  if (e.basicInfo.classification) {
    context += `  分類: ${e.basicInfo.classification}\n`
  }
  context += `  概要: ${e.basicInfo.summary}\n`
  context += `  詳細: ${e.basicInfo.description}\n`
  context += `  生息地: ${e.basicInfo.habitat}\n`
  if (e.basicInfo.distribution) {
    context += `  分布: ${e.basicInfo.distribution}\n`
  }
  if (e.basicInfo.timeOfActivity) {
    context += `  活動時間: ${e.basicInfo.timeOfActivity}\n`
  }
  if (e.basicInfo.frequency) {
    context += `  出現頻度: ${e.basicInfo.frequency}\n`
  }
  context += '\n'
  
  // 外見
  if (e.appearance) {
    context += `◆外見\n`
    context += `  全体的な描写: ${e.appearance.overallDescription}\n`
    context += `  サイズ: ${e.appearance.size}\n`
    if (e.appearance.height) {
      context += `  高さ: ${e.appearance.height}\n`
    }
    if (e.appearance.weight) {
      context += `  重さ: ${e.appearance.weight}\n`
    }
    context += `  体型: ${e.appearance.bodyType}\n`
    if (e.appearance.colorScheme) {
      context += `  色調: ${e.appearance.colorScheme}\n`
    }
    if (e.appearance.distinctiveFeatures?.length > 0) {
      context += `  特徴的な部位: ${e.appearance.distinctiveFeatures.join('、')}\n`
    }
    if (e.appearance.unusualTraits && e.appearance.unusualTraits.length > 0) {
      context += `  異常な特性: ${e.appearance.unusualTraits?.join('、')}\n`
    }
    context += `  視覚的インパクト: ${e.appearance.visualImpact}\n`
    context += `  初見の恐怖: ${e.appearance.firstImpressionFear}\n`
    if (e.appearance.sounds && e.appearance.sounds.length > 0) {
      context += `  音: ${e.appearance.sounds?.join('、')}\n`
    }
    context += '\n'
  }
  
  // 能力
  if (e.abilities) {
    context += `◆能力\n`
    
    // 攻撃パターン
    if (e.abilities.combatCapabilities?.attackPatterns?.length > 0) {
      context += `  攻撃パターン:\n`
      e.abilities.combatCapabilities.attackPatterns.slice(0, 3).forEach(attack => {
        context += `    ・${attack.name}: ${attack.description}\n`
        context += `      ダメージ: ${attack.damage}、範囲: ${attack.range}、頻度: ${attack.frequency}\n`
      })
    }
    
    // 特殊能力
    if (e.abilities.combatCapabilities?.specialAbilities?.length > 0) {
      context += `  特殊能力:\n`
      e.abilities.combatCapabilities.specialAbilities.slice(0, 3).forEach(ability => {
        context += `    ・${ability.name}: ${ability.description}\n`
        context += `      効果: ${ability.effect}\n`
      })
    }
    
    // 弱点
    if (e.abilities.weaknesses?.length > 0) {
      context += `  弱点:\n`
      e.abilities.weaknesses.forEach(weakness => {
        context += `    ・${weakness.type}: ${weakness.description} (効果: ${weakness.effectiveness})\n`
      })
    }
    
    // 耐性
    if (e.abilities.resistances?.length > 0) {
      context += `  耐性:\n`
      e.abilities.resistances.forEach(resistance => {
        context += `    ・${resistance.type}: ${resistance.description}\n`
      })
    }
    
    // 免疫
    if (e.abilities.immunities && e.abilities.immunities.length > 0) {
      context += `  免疫: ${e.abilities.immunities?.join('、')}\n`
    }
    context += '\n'
  }
  
  // 行動パターン
  if (e.behavior) {
    context += `◆行動パターン\n`
    context += `  AIパターン: ${e.behavior.aiPattern}\n`
    if (e.behavior.intelligenceDescription) {
      context += `  知能: ${e.behavior.intelligenceDescription}\n`
    }
    context += `  狩猟パターン: ${e.behavior.huntingPattern}\n`
    
    // トリガー
    if (e.behavior.triggers) {
      if (e.behavior.triggers.aggressive?.length > 0) {
        context += `  攻撃的トリガー: ${e.behavior.triggers.aggressive.join('、')}\n`
      }
      if (e.behavior.triggers.defensive?.length > 0) {
        context += `  防御的トリガー: ${e.behavior.triggers.defensive.join('、')}\n`
      }
      if (e.behavior.triggers.flee?.length > 0) {
        context += `  逃走トリガー: ${e.behavior.triggers.flee.join('、')}\n`
      }
    }
    
    // フェイズ
    if (e.behavior.phases && e.behavior.phases.length > 0) {
      context += `  戦闘フェイズ:\n`
      e.behavior.phases?.forEach(phase => {
        context += `    ・フェイズ${phase.phase} "${phase.name}": ${phase.description}\n`
      })
    }
    context += '\n'
  }
  
  // 伝承
  if (e.lore) {
    context += `◆伝承\n`
    if (e.lore.originStory) {
      context += `  起源: ${e.lore.originStory.narrative}\n`
      context += `  時代: ${e.lore.originStory.era}、場所: ${e.lore.originStory.location}\n`
    }
    context += '\n'
  }
  
  // 遭遇
  if (e.encounter) {
    context += `◆遭遇情報\n`
    if (e.encounter.conditions) {
      if (e.encounter.conditions.location?.length > 0) {
        context += `  遭遇場所: ${e.encounter.conditions.location.join('、')}\n`
      }
      if (e.encounter.conditions.time?.length > 0) {
        context += `  遭遇時間: ${e.encounter.conditions.time.join('、')}\n`
      }
    }
    context += '\n'
  }
  
  // GMノート
  if (e.gmNotes) {
    context += `◆GMノート\n`
    if (e.gmNotes.narrativeFunction) {
      context += `  物語上の機能: ${e.gmNotes.narrativeFunction}\n`
    }
    if (e.gmNotes.dramaticUsage && e.gmNotes.dramaticUsage.length > 0) {
      context += `  劇的な使用法: ${e.gmNotes.dramaticUsage?.join('、')}\n`
    }
    if (e.gmNotes.atmosphereTips && e.gmNotes.atmosphereTips.length > 0) {
      context += `  雰囲気のヒント: ${e.gmNotes.atmosphereTips?.join('、')}\n`
    }
  }
  
  return context.trim()
}

/**
 * プロットの完全な詳細コンテキストを生成
 * 世界観ビルダーや他のジェネレータで参照時に使用
 */
export function generatePlotContext(plot: SavedPlot): string {
  const p = plot.result
  let context = `【プロット：${p.title}】\n`
  
  // 基本情報
  context += `◆基本情報\n`
  context += `  タイトル: ${p.title}\n`
  context += `  前提: ${p.premise}\n`
  context += `  あらすじ: ${p.synopsis}\n`
  if (plot.input.genre) {
    context += `  ジャンル: ${plot.input.genre}\n`
  }
  if (plot.input.stage) {
    context += `  舞台: ${plot.input.stage}\n`
  }
  context += '\n'
  
  // テーマ
  if (p.themes?.length > 0) {
    context += `◆テーマ\n`
    p.themes.forEach(theme => {
      context += `  ・${theme}\n`
    })
    context += '\n'
  }
  
  // キーワード
  if (p.keywords?.length > 0) {
    context += `◆キーワード\n`
    context += `  ${p.keywords.join('、')}\n\n`
  }
  
  // 章構成
  if (p.structure?.length > 0) {
    context += `◆章構成\n`
    p.structure.forEach(chapter => {
      context += `  第${chapter.number}章「${chapter.title}」\n`
      context += `    ${chapter.summary}\n`
      if (chapter.type) {
        context += `    構成上の役割: ${chapter.type === 'introduction' ? '導入' : 
                                      chapter.type === 'development' ? '展開' :
                                      chapter.type === 'climax' ? 'クライマックス' : '結末'}\n`
      }
    })
    context += '\n'
  }
  
  // 登場人物
  if (p.characters?.length > 0) {
    context += `◆主要登場人物\n`
    p.characters.forEach(char => {
      context += `  ・${char.name}（${char.role}）\n`
      if (char.description) {
        context += `    ${char.description}\n`
      }
    })
    context += '\n'
  }
  
  return context.trim()
}

/**
 * 関係性の詳細コンテキストを生成
 */
export function generateRelationshipContext(relationship: SavedRelationship): string {
  const r = relationship.result
  let context = `【関係性】\n`
  context += `概要: ${r.summary}\n`
  context += `登場人物数: ${r.characters.length}名\n`
  
  if (r.characters?.length > 0) {
    context += `◆登場人物\n`
    r.characters.forEach(char => {
      context += `  ・${char.name}\n`
      if (char.profile) {
        context += `    プロフィール: ${char.profile}\n`
      }
      if (char.personality) {
        context += `    性格: ${char.personality}\n`
      }
      if (char.background) {
        context += `    背景: ${char.background}\n`
      }
    })
  }
  
  if (r.relationships?.length > 0) {
    context += `◆関係性詳細\n`
    r.relationships.forEach(rel => {
      // source/targetベースの新しい形式
      const sourceChar = r.characters.find(c => c.id === rel.source)
      const targetChar = r.characters.find(c => c.id === rel.target)
      if (sourceChar && targetChar) {
        context += `  ${sourceChar.name} → ${targetChar.name}:\n`
        if (rel.fromSourceToTarget) {
          context += `    ${sourceChar.name}から: ${rel.fromSourceToTarget}\n`
        }
        if (rel.fromTargetToSource) {
          context += `    ${targetChar.name}から: ${rel.fromTargetToSource}\n`
        }
        if (rel.strength) {
          context += `    関係の強さ: ${rel.strength}/10\n`
        }
        if (rel.description) {
          context += `    詳細: ${rel.description}\n`
        }
      }
    })
  }
  
  return context.trim()
}

/**
 * Wikipedia記事の詳細コンテキストを生成
 */
export function generateWikipediaContext(wikipedia: SavedWikipedia): string {
  const w = wikipedia.result
  let context = `【架空事典：${w.title}】\n`
  context += `カテゴリ: ${w.category}\n`
  
  if (w.leadSection) {
    context += `概要: ${w.leadSection}\n`
  }
  
  if (w.sections?.length > 0) {
    context += `◆セクション\n`
    w.sections.forEach(section => {
      context += `  ${section.title}: ${section.content.substring(0, 200)}...\n`
    })
  }
  
  if (w.infobox) {
    context += `◆基本情報\n`
    Object.entries(w.infobox).forEach(([key, value]) => {
      if (typeof value === 'string') {
        context += `  ${key}: ${value}\n`
      }
    })
  }
  
  return context.trim()
}

/**
 * アイテムの完全な詳細コンテキストを生成
 * 世界観ビルダーや他のジェネレータで参照時に使用
 */
export function generateItemContext(item: SavedItem): string {
  const i = item.result
  let context = `【アイテム：${i.basicInfo.name}】\n`
  
  // 基本情報
  context += `◆基本情報\n`
  context += `  名前: ${i.basicInfo.name}\n`
  if (i.basicInfo.aliases && i.basicInfo.aliases.length > 0) {
    context += `  別名: ${i.basicInfo.aliases?.join('、')}\n`
  }
  context += `  タイプ: ${i.basicInfo.type}\n`
  context += `  カテゴリ: ${i.basicInfo.category}\n`
  context += `  レア度: ${i.basicInfo.rarity}\n`
  context += `  概要: ${i.basicInfo.summary}\n`
  context += `  詳細: ${i.basicInfo.description}\n`
  if (i.basicInfo.detailedDescription) {
    context += `  物語的説明: ${i.basicInfo.detailedDescription}\n`
  }
  if (i.basicInfo.significance) {
    context += `  重要性: ${i.basicInfo.significance}\n`
  }
  context += '\n'
  
  // 外見
  if (i.appearance) {
    context += `◆外見\n`
    context += `  見た目: ${i.appearance.visualDescription}\n`
    if (i.appearance.material) {
      context += `  素材: ${i.appearance.material}\n`
    }
    if (i.appearance.size) {
      context += `  サイズ: ${i.appearance.size}\n`
    }
    if (i.appearance.weight) {
      context += `  重量: ${i.appearance.weight}\n`
    }
    if (i.appearance.color) {
      context += `  色: ${i.appearance.color}\n`
    }
    if (i.appearance.texture) {
      context += `  質感: ${i.appearance.texture}\n`
    }
    if (i.appearance.condition) {
      context += `  状態: ${i.appearance.condition}\n`
    }
    if (i.appearance.age) {
      context += `  経年: ${i.appearance.age}\n`
    }
    if (i.appearance.distinctiveFeatures?.length > 0) {
      context += `  特徴: ${i.appearance.distinctiveFeatures.join('、')}\n`
    }
    if (i.appearance.manufacturer) {
      context += `  製造者: ${i.appearance.manufacturer}\n`
    }
    if (i.appearance.markings && i.appearance.markings.length > 0) {
      context += `  刻印: ${i.appearance.markings?.join('、')}\n`
    }
    context += '\n'
  }
  
  // 効果
  if (i.effects) {
    context += `◆効果\n`
    
    // 主要効果
    if (i.effects.primaryEffect) {
      const pe = i.effects.primaryEffect
      context += `  主要効果: ${pe.description}\n`
      if (pe.type) {
        context += `    タイプ: ${pe.type}\n`
      }
      if (pe.magnitude) {
        context += `    強さ: ${pe.magnitude}\n`
      }
      if (pe.duration) {
        context += `    持続時間: ${pe.duration}\n`
      }
      if (pe.range) {
        context += `    範囲: ${pe.range}\n`
      }
      if (pe.target) {
        context += `    対象: ${pe.target}\n`
      }
    }
    
    // 副次効果
    if (i.effects.secondaryEffects && i.effects.secondaryEffects.length > 0) {
      context += `  副次効果:\n`
      i.effects.secondaryEffects?.slice(0, 2).forEach(se => {
        context += `    ・${se.type}: ${se.description}\n`
        if (se.condition) {
          context += `      発動条件: ${se.condition}\n`
        }
      })
    }
    
    // 副作用
    if (i.effects.sideEffects && i.effects.sideEffects.length > 0) {
      context += `  副作用:\n`
      i.effects.sideEffects?.slice(0, 2).forEach(side => {
        context += `    ・${side.effect} (発生確率: ${side.chance}, 重篤度: ${side.severity})\n`
      })
    }
    
    // 使用方法
    if (i.effects.usageMethod) {
      context += `  使用方法: ${i.effects.usageMethod}\n`
    }
    if (i.effects.activationCondition) {
      context += `  起動条件: ${i.effects.activationCondition}\n`
    }
    if (i.effects.cooldown) {
      context += `  クールダウン: ${i.effects.cooldown}\n`
    }
    if (i.effects.charges !== undefined) {
      context += `  チャージ数: ${i.effects.charges}\n`
    }
    if (i.effects.rechargeMethod) {
      context += `  充填方法: ${i.effects.rechargeMethod}\n`
    }
    if (i.effects.limitations?.length > 0) {
      context += `  制限: ${i.effects.limitations.join('、')}\n`
    }
    if (i.effects.requirements && i.effects.requirements.length > 0) {
      context += `  必要条件: ${i.effects.requirements?.join('、')}\n`
    }
    context += '\n'
  }
  
  // 入手方法
  if (i.acquisition) {
    context += `◆入手方法\n`
    if (i.acquisition.obtainMethod?.length > 0) {
      context += `  入手方法: ${i.acquisition.obtainMethod.join('、')}\n`
    }
    if (i.acquisition.locations && i.acquisition.locations.length > 0) {
      context += `  入手場所: ${i.acquisition.locations?.join('、')}\n`
    }
    if (i.acquisition.dropRate) {
      context += `  ドロップ率: ${i.acquisition.dropRate}\n`
    }
    if (i.acquisition.estimatedValue) {
      context += `  推定価値: ${i.acquisition.estimatedValue}\n`
    }
    if (i.acquisition.rarity) {
      context += `  希少度: ${i.acquisition.rarity}\n`
    }
    if (i.acquisition.availability) {
      context += `  入手可能性: ${i.acquisition.availability}\n`
    }
    context += '\n'
  }
  
  // 伝承
  if (i.lore) {
    context += `◆伝承\n`
    
    // 起源物語
    if (i.lore.originStory) {
      const os = i.lore.originStory
      context += `  起源:\n`
      if (os.narrative) {
        context += `    物語: ${os.narrative}\n`
      }
      if (os.era) {
        context += `    時代: ${os.era}\n`
      }
      if (os.location) {
        context += `    場所: ${os.location}\n`
      }
      if (os.creator) {
        context += `    創造者: ${os.creator}\n`
      }
      if (os.purpose) {
        context += `    目的: ${os.purpose}\n`
      }
    }
    
    // 伝説
    if (i.lore.legends?.length > 0) {
      context += `  伝説:\n`
      i.lore.legends.slice(0, 2).forEach(legend => {
        context += `    ・「${legend.title}」\n`
        if (legend.story) {
          context += `      ${legend.story.substring(0, 150)}...\n`
        }
      })
    }
    
    // 有名な所有者
    if (i.lore.famousOwners?.length > 0) {
      context += `  有名な所有者:\n`
      i.lore.famousOwners.slice(0, 2).forEach(owner => {
        context += `    ・${owner.name} (${owner.title || owner.era})\n`
        if (owner.deeds) {
          context += `      功績: ${owner.deeds}\n`
        }
      })
    }
    
    // 関連アイテム
    if (i.lore.relatedItems && i.lore.relatedItems.length > 0) {
      context += `  関連アイテム: ${i.lore.relatedItems?.join('、')}\n`
    }
    
    // 文化的影響
    if (i.lore.culturalImpact) {
      context += `  文化的影響: ${i.lore.culturalImpact}\n`
    }
    
    // 象徴性
    if (i.lore.symbolism) {
      context += `  象徴: ${i.lore.symbolism}\n`
    }
    
    // 呪い
    if (i.lore.curses && i.lore.curses.length > 0) {
      context += `  呪い:\n`
      i.lore.curses?.forEach(curse => {
        context += `    ・${curse.description}\n`
        if (curse.breakingMethod) {
          context += `      解除方法: ${curse.breakingMethod}\n`
        }
      })
    }
  }
  
  return context.trim()
}

/**
 * コミュニティの詳細コンテキストを生成
 * 他のジェネレータで参照時に使用
 */
export function generateCommunityContext(community: SavedCommunity): string {
  const c = community.result
  let context = `【コミュニティ：${c.basicInfo.name}】\n`
  
  // 基本情報
  context += `◆基本情報\n`
  context += `  名前: ${c.basicInfo.name}\n`
  context += `  タイプ: ${c.basicInfo.type}\n`
  context += `  規模: ${c.basicInfo.scale}\n`
  context += `  人口: ${c.basicInfo.population.toLocaleString()}人\n`
  context += `  雰囲気: ${c.basicInfo.atmosphere}\n\n`
  
  // 特徴
  context += `◆特徴\n`
  context += `  地理: ${c.features.geography}\n`
  context += `  施設: ${c.features.facilities}\n`
  context += `  文化: ${c.features.culture}\n`
  context += `  問題: ${c.features.problems}\n\n`
  
  // 住民
  context += `◆住民\n`
  context += `  総人口: ${c.residents.totalPopulation.toLocaleString()}人\n`
  context += `  特徴: ${c.residents.characteristics}\n`
  
  // 著名な住民
  if (Array.isArray(c.residents.notableResidents) && c.residents.notableResidents.length > 0) {
    context += `  著名な住民:\n`
    c.residents.notableResidents.slice(0, 3).forEach((r: any) => {
      context += `    ・${r.name}（${r.occupation}）: ${r.description}\n`
    })
  }
  context += '\n'
  
  // 政治・経済
  context += `◆政治・経済\n`
  context += `  政治: ${c.features?.politics || (c as any).politicsEconomy?.politics || ''}\n`
  context += `  経済: ${c.features?.economy || (c as any).politicsEconomy?.economy || ''}\n\n`
  
  // 歴史
  context += `◆歴史\n`
  context += `  創立物語: ${c.history.foundingStory}\n`
  if (Array.isArray(c.history.timeline) && c.history.timeline.length > 0) {
    context += `  年表:\n`
    c.history.timeline.slice(0, 5).forEach((event: any) => {
      context += `    ・${event.year}: ${event.event}\n`
    })
  }
  
  return context.trim()
}

/**
 * ソサエティ（組織）の詳細コンテキストを生成
 * 他のジェネレータで参照時に使用
 */
export function generateSocietyContext(society: SavedSociety): string {
  const s = society.result
  let context = `【組織：${s.basicInfo.name}】\n`
  
  // 基本情報
  context += `◆基本情報\n`
  context += `  名前: ${s.basicInfo.name}\n`
  if (s.basicInfo.aliases?.length) {
    context += `  別名: ${s.basicInfo.aliases.join('、')}\n`
  }
  context += `  タイプ: ${s.basicInfo.type}\n`
  context += `  規模: ${s.basicInfo.scale}\n`
  if (s.basicInfo.foundingDate) {
    context += `  設立: ${s.basicInfo.foundingDate}\n`
  }
  if (s.basicInfo.motto) {
    context += `  モットー: ${s.basicInfo.motto}\n`
  }
  context += `  目的: ${s.basicInfo.purpose}\n`
  context += `  概要: ${s.basicInfo.summary}\n\n`
  
  // 組織構造
  context += `◆組織構造\n`
  context += `  メンバー数: ${s.structure.memberCount}\n`
  if (Array.isArray(s.structure.hierarchy) && s.structure.hierarchy.length > 0) {
    context += `  階級制度:\n`
    s.structure.hierarchy.slice(0, 3).forEach(level => {
      context += `    ・Lv${level.level} ${level.title}（${level.count}）: ${level.responsibilities}\n`
    })
  }
  if (Array.isArray(s.structure.departments) && s.structure.departments.length > 0) {
    context += `  主要部門:\n`
    s.structure.departments.slice(0, 3).forEach(dept => {
      context += `    ・${dept.name}（${dept.size}）: ${dept.purpose}\n`
    })
  }
  context += '\n'
  
  // メンバー
  if (Array.isArray(s.members?.importantMembers) && s.members.importantMembers.length > 0) {
    context += `◆重要メンバー\n`
    s.members.importantMembers.slice(0, 3).forEach((member, index) => {
      context += `  ${index + 1}. ${member.name}（${member.role}）\n`
      if (index === 0) {
        context += `     ${member.description.substring(0, 100)}...\n`
      }
    })
  }
  context += '\n'
  
  // 特徴
  if (s.features) {
    if (s.features.activities) {
      context += `◆活動内容\n`
      context += `  ${s.features.activities.substring(0, 150)}...\n`
    }
    if (s.features.culture) {
      context += `◆組織文化\n`
      context += `  ${s.features.culture.substring(0, 150)}...\n`
    }
    if (s.features.relations) {
      context += `◆他組織との関係\n`
      context += `  ${s.features.relations.substring(0, 150)}...\n`
    }
    if (s.features.secrets) {
      context += `◆秘密\n`
      context += `  ${s.features.secrets.substring(0, 100)}...\n`
    }
  }
  context += '\n'
  
  // エピソード（代表的なもののみ）
  if (Array.isArray(s.episodes?.episodes) && s.episodes.episodes.length > 0) {
    context += `◆エピソード\n`
    s.episodes.episodes.slice(0, 2).forEach(episode => {
      context += `  ・${episode.title}: ${episode.content.substring(0, 100)}...\n`
    })
  }
  
  return context.trim()
}

/**
 * ダンジョンの完全な詳細コンテキストを生成
 * 他のジェネレータで参照時に使用
 */
export function generateDungeonContext(dungeon: any): string {
  // 旧形式のデータを新形式に変換
  const convertedResult = convertOldDungeonToNew(dungeon.result)
  const d = convertedResult
  let context = `【ダンジョン：${d.basicInfo.name}】\n`
  
  // 基本情報
  context += `◆基本情報\n`
  context += `  タイプ: ${d.basicInfo.type}\n`
  context += `  難易度: ${d.basicInfo.difficulty}\n`
  context += `  説明: ${d.basicInfo.description}\n`
  // featuresは新形式では文字列
  if (d.basicInfo.features) {
    context += `  特徴: ${d.basicInfo.features}\n`
  }
  context += '\n'
  
  // 構造
  if (d.structure) {
    context += `◆構造\n`
    context += `  総部屋数: ${d.structure.totalRooms}\n`
    if (d.structure.floors?.length) {
      context += `  フロア数: ${d.structure.floors.length}\n`
      context += `  レイアウト: ${d.structure.layout.type}\n`
      context += `  主要フロア:\n`
      d.structure.floors.slice(0, 3).forEach((floor: any) => {
        context += `    ・${floor.name || `第${floor.level}フロア`}: ${floor.theme}\n`
        if (floor.ambiance) {
          context += `      雰囲気: ${floor.ambiance.substring(0, 50)}...\n`
        }
      })
    }
  }
  context += '\n'
  
  // コンテンツ
  if (d.contents) {
    if (d.contents.enemies) {
      context += `◆敵\n`
      context += `  ${d.contents.enemies.substring(0, 150)}...\n\n`
    }
    
    if (d.contents.bosses) {
      context += `◆ボス\n`
      context += `  ${d.contents.bosses.substring(0, 150)}...\n\n`
    }
    
    if (d.contents.treasures) {
      context += `◆宝物\n`
      context += `  ${d.contents.treasures.substring(0, 100)}...\n\n`
    }
    
    if (d.contents.traps) {
      context += `◆トラップ\n`
      context += `  ${d.contents.traps.substring(0, 100)}...\n\n`
    }
    
    if (d.contents.puzzles) {
      context += `◆パズル\n`
      context += `  ${d.contents.puzzles.substring(0, 100)}...\n\n`
    }
  }
  
  // 逸話（新形式対応）
  if (d.anecdotes) {
    if (d.anecdotes.historyAndLore) {
      context += `◆歴史と伝承\n`
      context += `  ${d.anecdotes.historyAndLore.substring(0, 200)}...\n\n`
    }
    
    if (d.anecdotes.rumors) {
      context += `◆噂\n`
      context += `  ${d.anecdotes.rumors.substring(0, 150)}...\n\n`
    }
    
    if (d.anecdotes.events) {
      context += `◆過去の出来事\n`
      context += `  ${d.anecdotes.events.substring(0, 150)}...\n\n`
    }
    
    if (d.anecdotes.hiddenStory) {
      context += `◆隠された物語\n`
      context += `  ${d.anecdotes.hiddenStory.substring(0, 150)}...\n\n`
    }
  }
  
  return context.trim()
}

/**
 * 要約の完全な詳細コンテキストを生成
 * 他のジェネレータで参照時に使用
 */
export function generateSummaryContext(summary: SavedSummary): string {
  const s = summary.result
  
  return `【要約されたテキスト】
${s.summary}

【メタデータ】
元の文字数: ${s.metadata.originalLength}
要約後の文字数: ${s.metadata.summaryLength}
圧縮率: ${s.metadata.compressionRatio}
テキストタイプ: ${s.metadata.textType}
生成日時: ${s.metadata.generatedAt}`
}

/**
 * 要約結果を特定のジェネレータ用に最適化
 */
export function optimizeForGenerator(
  summary: SummaryResult, 
  targetGenerator: string
): string {
  switch(targetGenerator) {
    case 'plot':
      return `【物語の流れ】\n${summary.summary}`
    case 'character':
      return `【物語での行動】\n${summary.summary}`
    case 'scene':
      return summary.summary
    case 'item':
      return `【背景情報】\n${summary.summary}`
    case 'relationship':
      return `【人物関係の文脈】\n${summary.summary}`
    default:
      return summary.summary
  }
}
